// Ideal Postcodes client - calls a server-side proxy edge function so the
// API key is never exposed to the browser bundle.
import { supabase } from "@/integrations/supabase/client";

export interface IdealAddress {
  line_1: string;
  line_2: string;
  line_3: string;
  post_town: string;
  postcode: string;
  county: string;
  country: string;
  latitude: number;
  longitude: number;
  uprn: string;
}

export interface AutocompleteSuggestion {
  id: string;
  suggestion: string;
  udprn: number;
  address: string;
}

export class RateLimitError extends Error {
  constructor(message = "Too many postcode lookups. Please slow down and try again shortly.") {
    super(message);
    this.name = "RateLimitError";
  }
}

async function callProxy(body: Record<string, unknown>): Promise<any | null> {
  try {
    const { data, error } = await supabase.functions.invoke("postcode-lookup", { body });
    if (error) {
      const status = (error as any)?.context?.status;
      if (status === 429) {
        throw new RateLimitError();
      }
      return null;
    }
    return data;
  } catch (e) {
    if (e instanceof RateLimitError) throw e;
    return null;
  }
}

export const autocompleteAddress = async (query: string): Promise<AutocompleteSuggestion[]> => {
  if (query.length < 2) return [];
  const json = await callProxy({ op: "autocomplete", q: query });
  const hits = json?.result?.hits || [];
  return hits.map((h: any) => ({
    id: h.id,
    suggestion: h.suggestion,
    udprn: h.udprn,
    address: h.suggestion,
  })) as AutocompleteSuggestion[];
};

export const resolveAddress = async (udprn: string): Promise<IdealAddress | null> => {
  const json = await callProxy({ op: "udprn", udprn });
  return (json?.result as IdealAddress) ?? null;
};

// Free postcodes.io fallback for logged-out visitors (proxy requires sign-in).
const postcodesIoLookup = async (postcode: string): Promise<IdealAddress[]> => {
  try {
    const res = await fetch(
      `https://api.postcodes.io/postcodes/${encodeURIComponent(postcode.replace(/\s+/g, ""))}`,
    );
    if (!res.ok) return [];
    const r = (await res.json())?.result;
    if (!r) return [];
    return [{
      line_1: "", line_2: "", line_3: "",
      post_town: r.admin_district || r.parish || "",
      postcode: r.postcode,
      county: r.admin_county || "",
      country: r.country || "",
      latitude: r.latitude,
      longitude: r.longitude,
      uprn: "",
    }];
  } catch {
    return [];
  }
};

export const lookupPostcode = async (postcode: string): Promise<IdealAddress[]> => {
  const json = await callProxy({ op: "postcode", postcode });
  const result = (json?.result || []) as IdealAddress[];
  if (result.length > 0) return result;
  return postcodesIoLookup(postcode);
};

export interface LatLng {
  latitude: number;
  longitude: number;
}

// Free fallback geocoder (postcodes.io) used when the primary provider is
// unavailable or returns no coordinates, so radius searches keep working.
const geocodeViaPostcodesIo = async (postcode: string): Promise<LatLng | null> => {
  try {
    const res = await fetch(
      `https://api.postcodes.io/postcodes/${encodeURIComponent(postcode.replace(/\s+/g, ""))}`,
    );
    if (!res.ok) return null;
    const json = await res.json();
    const lat = json?.result?.latitude;
    const lon = json?.result?.longitude;
    if (typeof lat !== "number" || typeof lon !== "number") return null;
    return { latitude: lat, longitude: lon };
  } catch {
    return null;
  }
};

export const geocodePostcode = async (postcode: string): Promise<LatLng | null> => {
  const addresses = await lookupPostcode(postcode);
  const first = addresses[0];
  if (first?.latitude && first?.longitude) {
    return { latitude: first.latitude, longitude: first.longitude };
  }
  return geocodeViaPostcodesIo(postcode);
};


export const bulkGeocodePostcodes = async (postcodes: string[]): Promise<Map<string, LatLng>> => {
  const map = new Map<string, LatLng>();
  if (postcodes.length === 0) return map;
  const unique = [...new Set(postcodes.map((p) => p.replace(/\s/g, "").toUpperCase()))];
  await Promise.all(
    unique.map(async (pc) => {
      const result = await geocodePostcode(pc);
      if (result) map.set(pc, result);
    }),
  );
  return map;
};

// In-memory cache for reverse-geocoding by coarse lat/lon (~11m at 4dp).
// Repeated "Use my location" clicks from roughly the same spot avoid a round
// trip. Entries expire after 10 minutes to stay fresh as the user moves.
const NEAREST_TTL_MS = 10 * 60 * 1000;
const NEAREST_MAX_ENTRIES = 100;
const nearestCache = new Map<string, { postcode: string | null; expires: number }>();

const nearestKey = (lat: number, lon: number) =>
  `${lat.toFixed(4)},${lon.toFixed(4)}`;

export const nearestPostcode = async (lat: number, lon: number): Promise<string | null> => {
  const key = nearestKey(lat, lon);
  const now = Date.now();
  const cached = nearestCache.get(key);
  if (cached && cached.expires > now) {
    // Refresh LRU position
    nearestCache.delete(key);
    nearestCache.set(key, cached);
    return cached.postcode;
  }

  const json = await callProxy({ op: "nearest", lat, lon });
  let postcode: string | null = json?.result?.[0]?.postcode ?? null;
  if (!postcode) {
    try {
      const res = await fetch(`https://api.postcodes.io/postcodes?lon=${lon}&lat=${lat}&limit=1`);
      if (res.ok) postcode = (await res.json())?.result?.[0]?.postcode ?? null;
    } catch { /* ignore */ }
  }

  // Only cache successful lookups so transient errors can be retried.
  if (postcode) {
    nearestCache.set(key, { postcode, expires: now + NEAREST_TTL_MS });
    if (nearestCache.size > NEAREST_MAX_ENTRIES) {
      const oldest = nearestCache.keys().next().value;
      if (oldest !== undefined) nearestCache.delete(oldest);
    }
  }
  return postcode;
};

export const normalizePostcode = (pc: string): string =>
  pc.replace(/\s/g, "").toUpperCase();

export const validatePostcode = async (postcode: string): Promise<boolean> => {
  const json = await callProxy({ op: "validate", postcode });
  if (json?.valid === true) return true;
  return (await postcodesIoLookup(postcode)).length > 0;
};

export const formatAddress = (addr: IdealAddress): string =>
  [addr.line_1, addr.line_2, addr.line_3, addr.post_town].filter(Boolean).join(", ");
